import * as React from 'react';
import { useState } from 'react';

import styled from '@emotion/styled';
import moment from 'moment-timezone';

import { Button, Alert } from '@shared/components/bootstrap';

import { SingleSelectFormControl } from '@admin/components/fields/single_select_form_control';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { Timestamp } from '@admin/components/timestamp';
import { accountURL, logisticsDetailedLoadURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import { useLogisticsPalletInfoQuery, LogisticsPalletInfoQuery } from '@admin/schema';

import { useEditPalletSkuAndLoad } from './edit';

const Link = styled.a`
  color: #357edd;
`;

const EditButton = styled(Button)`
  padding: 4px 8px;
  font-size: 12px;
`;

const SaveButton = styled(Button)`
  padding: 4px 8px;
  font-size: 11px;
`;

const CancelButton = styled(Button)`
  padding: 4px 8px;
  font-size: 11px;
  margin-right: 4px;
`;

const LoadingText = styled.div`
  font-size: 13px;
  color: #999;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const TableWrapper = styled.div`
  overflow: visible;

  .table {
    overflow: visible;
  }

  tbody td {
    overflow: visible;
  }

  /* Ensure dropdown options appear on top of the table */
  [role='combobox'] {
    z-index: 1000;
  }

  /* Make dropdown options not clip */
  .select-dropdown,
  .dropdown-menu {
    position: fixed !important;
  }
`;

type PalletInventoryLoadsType = LogisticsPalletInfoQuery['pallet']['loads'];
type PalletInventoriesType = LogisticsPalletInfoQuery['pallet']['palletInventories'];

interface InventoryRowProps {
  palletInventory: PalletInventoriesType[number];
  index: number;
  isEditing: boolean;
  palletId: string;
  canEdit: boolean;
  onEditClick: (index: number) => void;
  onCancelEdit: () => void;
  onSuccessEdit: () => void;
  isAnyEditing: boolean;
}

const InventoryRow: React.FC<InventoryRowProps> = ({
  palletInventory,
  index,
  isEditing,
  palletId,
  canEdit,
  onEditClick,
  onCancelEdit,
  onSuccessEdit,
  isAnyEditing,
}) => {
  // Hook is always called, not conditionally
  const editState = useEditPalletSkuAndLoad(
    palletId,
    palletInventory.sku.id,
    palletInventory.sku.account.id,
    onSuccessEdit,
  );

  if (!isEditing) {
    return (
      <tr>
        <td className="col-md-2 text-left">
          <Link href={accountURL({ id: palletInventory.sku.account.id })}>
            {' '}
            {palletInventory.sku.account.customer.name}
          </Link>
        </td>
        <td className="col-md-2 text-left">{palletInventory.sku.value}</td>
        <td className="col-md-2 text-left">{palletInventory.sku.description}</td>
        <td className="col-md-1 text-left">{palletInventory.quantity}</td>
        <td className="col-md-1 text-center">
          {canEdit ? (
            <EditButton kind="info" onClick={() => onEditClick(index)} disabled={isAnyEditing} aria-label="Edit SKU">
              Edit
            </EditButton>
          ) : null}
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="col-md-2 text-left">
        <Link href={accountURL({ id: palletInventory.sku.account.id })}>
          {' '}
          {palletInventory.sku.account.customer.name}
        </Link>
      </td>
      <td className="col-md-2 text-left">
        {editState.accountSkusQuery.loading ? (
          <LoadingText>Loading...</LoadingText>
        ) : (
          <SingleSelectFormControl
            id={`sku_select_${index}`}
            value={editState.selectedNewSkuId}
            onChange={(id) => {
              if (id) editState.setSelectedNewSkuId(id);
            }}
            options={editState.skuOptions}
            disabled={editState.loading || editState.accountSkusQuery.loading}
          />
        )}
      </td>
      <td className="col-md-2 text-left">{palletInventory.sku.description}</td>
      <td className="col-md-1 text-left">{palletInventory.quantity}</td>
      <td className="col-md-1 text-center">
        <ActionContainer>
          <CancelButton kind="default" onClick={onCancelEdit} disabled={editState.loading}>
            Cancel
          </CancelButton>
          <SaveButton
            kind="primary"
            loading={editState.loading}
            onClick={() => editState.handleSubmit()}
            disabled={!editState.selectedNewSkuId || editState.loading}
          >
            Save
          </SaveButton>
        </ActionContainer>
      </td>
    </tr>
  );
};

const LogisticsPalletLoadsPanel: React.FC<{ loads: PalletInventoryLoadsType }> = ({ loads }) => {
  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Associated 3PL Loads</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {loads && loads.length ? (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th className="col-md-4 text-left">Load Type</th>
                  <th className="col-md-4 text-left">Load Number</th>
                  <th className="col-md-4 text-left">Appointment Date</th>
                </tr>
              </thead>
              <tbody>
                {[...loads]
                  .sort((a, b) => {
                    const aDate = a.appointmentDate ? moment(a.appointmentDate).valueOf() : null;
                    const bDate = b.appointmentDate ? moment(b.appointmentDate).valueOf() : null;

                    if (aDate === null && bDate === null) return 0;
                    if (aDate === null) return 1;
                    if (bDate === null) return -1;

                    return bDate - aDate;
                  })
                  .map((load) => (
                    <tr key={load.id}>
                      <td className="col-md-4 text-left">
                        <strong>{load.type}</strong>
                      </td>
                      <td className="col-md-4 text-left">
                        <Link href={logisticsDetailedLoadURL({ id: load.id })} target="_blank">
                          {' '}
                          {load.number}
                        </Link>
                      </td>
                      <td className="col-md-4 text-left">
                        {load.appointmentDate ? (
                          <Timestamp value={load.appointmentDate} timezone={load.warehouse.tz} />
                        ) : (
                          'Not set'
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center">no associated loads found</p>
        )}
      </Panel.Body>
    </Panel>
  );
};

const LogisticsPalletInventoryPanel: React.FC<{
  palletInventories: PalletInventoriesType;
  palletId: string;
}> = ({ palletInventories, palletId }) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const inventoriesWithQuantity = palletInventories.filter((pi) => pi.quantity > 0);
  const canEdit = inventoriesWithQuantity.length > 0;

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  const handleSuccessEdit = () => {
    setEditingIndex(null);
  };

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>3PL Inventory</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        {inventoriesWithQuantity && inventoriesWithQuantity.length ? (
          <TableWrapper className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th className="col-md-2 text-left">Account</th>
                  <th className="col-md-2 text-left">SKU</th>
                  <th className="col-md-2 text-left">SKU Description</th>
                  <th className="col-md-1 text-left">Quantity</th>
                  <th className="col-md-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventoriesWithQuantity.map((palletInventory, index) => (
                  <InventoryRow
                    key={palletInventory.sku.id}
                    palletInventory={palletInventory}
                    index={index}
                    isEditing={editingIndex === index}
                    palletId={palletId}
                    canEdit={canEdit}
                    onEditClick={handleEditClick}
                    onCancelEdit={handleCancelEdit}
                    onSuccessEdit={handleSuccessEdit}
                    isAnyEditing={editingIndex !== null}
                  />
                ))}
              </tbody>
            </table>
          </TableWrapper>
        ) : (
          <p className="text-center">no associated inventory found</p>
        )}
      </Panel.Body>
    </Panel>
  );
};

export const LogisticsPalletInfoPanels: React.FC<{ id: string }> = ({ id }) => {
  const { data, loading } = useLogisticsPalletInfoQuery({ client, variables: { id } });

  if (loading || !data) {
    return <Spinner />;
  }

  const palletInventories = data.pallet.palletInventories;
  const loads = data.pallet.loads;

  return (
    <>
      <LogisticsPalletInventoryPanel palletInventories={palletInventories} palletId={id} />
      <LogisticsPalletLoadsPanel loads={loads} />
    </>
  );
};
